import { useState } from "react";
import { Play, ExternalLink, ChevronsUpDown, Cookie } from "lucide-react";
import { Reveal } from "./Reveal";
import { useConsent, openCookiePreferences } from "../lib/consent";

const ConsentGate = ({ video }) => (
    <div
        className="absolute inset-0 flex flex-col items-center justify-center bg-[#0B1F3A] p-6 text-center text-white"
        data-testid="video-consent-gate"
    >
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10">
            <Cookie className="h-6 w-6 text-[#E30613]" />
        </span>
        <h4 className="font-display mt-4 text-lg font-bold sm:text-xl">
            Accetta i cookie per vedere il video
        </h4>
        <p className="mt-2 max-w-md text-sm text-white/70">
            Il video "{video.model} · {video.title}" è ospitato su YouTube (youtube-nocookie.com).
            Per caricarlo serve il tuo consenso ai contenuti esterni.
        </p>
        <div className="mt-5 flex flex-col gap-2.5 sm:flex-row">
            <button
                type="button"
                onClick={openCookiePreferences}
                data-testid="video-consent-button"
                className="rounded-full bg-[#E30613] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#C20410]"
            >
                Gestisci preferenze cookie
            </button>
            <a
                href={`https://www.youtube.com/watch?v=${video.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1.5 rounded-full border border-white/30 px-5 py-2.5 text-sm font-bold text-white hover:bg-white/10"
            >
                <ExternalLink className="h-3.5 w-3.5" /> Guarda su YouTube
            </a>
        </div>
    </div>
);

export const YouTubePlayer = ({ video, brand, autoplay = false, className = "" }) => {
    const [playing, setPlaying] = useState(autoplay);
    const { consent } = useConsent();
    if (!consent.media) {
        return (
            <div className={`relative aspect-video w-full overflow-hidden rounded-3xl bg-[#0B1F3A] ${className}`} data-testid="video-player">
                <ConsentGate video={video} />
            </div>
        );
    }
    return (
        <div
            className={`relative aspect-video w-full overflow-hidden rounded-3xl bg-[#0B1F3A] ${className}`}
            data-testid="video-player"
        >
            {playing ? (
                <>
                    <iframe
                        key={video.id}
                        src={`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&rel=0&modestbranding=1`}
                        title={video.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="absolute inset-0 h-full w-full"
                        data-testid="video-iframe"
                    />
                    <a
                        href={`https://www.youtube.com/watch?v=${video.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="video-open-youtube-link"
                        className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-[11px] font-bold text-[#0B1F3A] shadow backdrop-blur transition-colors hover:bg-white"
                    >
                        <ExternalLink className="h-3 w-3" />
                        Apri su YouTube
                    </a>
                </>
            ) : (
                <button
                    type="button"
                    onClick={() => setPlaying(true)}
                    className="group absolute inset-0 h-full w-full"
                    aria-label={`Riproduci video: ${video.title}`}
                    data-testid="video-play-button"
                >
                    <img
                        src={`https://i.ytimg.com/vi/${video.id}/maxresdefault.jpg`}
                        onError={(e) => {
                            e.currentTarget.src = `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`;
                        }}
                        alt={video.title}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <span className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A]/80 via-[#0B1F3A]/10 to-transparent" />
                    <span className="absolute left-1/2 top-[42%] flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 sm:top-1/2 sm:h-20 sm:w-20 items-center justify-center rounded-full bg-[#E30613] text-white shadow-[0_20px_50px_-10px_rgba(227,6,19,0.6)] transition-transform duration-300 group-hover:scale-110">
                        <Play className="ml-1 h-8 w-8 fill-current" />
                    </span>
                    <span className="absolute bottom-6 left-6 right-6 text-left">
                        <span className="hidden text-[10px] font-bold uppercase tracking-[0.18em] text-[#E30613] sm:block">
                            Video ufficiale {brand}
                        </span>
                        <span className="font-display mt-1 block text-base font-bold text-white sm:text-xl">
                            {video.model} · {video.title}
                        </span>
                    </span>
                </button>
            )}
        </div>
    );
};

export const VideoGallery = ({ eyebrow, title, desc, brand, videos, testid, dark = false }) => {
    const [current, setCurrent] = useState(videos[0]);
    const [autoplay, setAutoplay] = useState(false);
    const { consent } = useConsent();

    const select = (v) => {
        setCurrent(v);
        setAutoplay(true);
    };

    return (
        <div className="mt-16" data-testid={testid}>
            <Reveal>
                <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
                    <div>
                        <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#E30613]">
                            {eyebrow}
                        </span>
                        <h3
                            className={`font-display mt-3 text-2xl font-extrabold tracking-tight sm:text-3xl ${
                                dark ? "text-white" : "text-[#0B1F3A]"
                            }`}
                        >
                            {title}
                        </h3>
                    </div>
                    <p className={`max-w-md text-sm ${dark ? "text-white/65" : "text-[#4F5D70]"}`}>
                        {desc}
                    </p>
                </div>
            </Reveal>
            <div className="mt-8 grid min-w-0 gap-6 lg:grid-cols-12">
                <Reveal className="min-w-0 lg:col-span-8">
                    <YouTubePlayer key={current.id} video={current} brand={brand} autoplay={autoplay} />
                </Reveal>
                <Reveal delay={0.1} className="min-w-0 lg:col-span-4">
                    <div className="relative min-w-0">
                    <ul
                        data-lenis-prevent
                        className="scroll-panel flex w-full max-w-full gap-3 overflow-x-auto pb-2 lg:max-h-[420px] lg:flex-col lg:overflow-y-auto lg:pr-2"
                        data-testid={`${testid}-playlist`}
                    >
                        {videos.map((v) => (
                            <li key={v.id} className="shrink-0 lg:shrink">
                                <button
                                    type="button"
                                    onClick={() => select(v)}
                                    data-testid={`${testid}-item-${v.key}`}
                                    className={`flex w-64 items-center gap-3 rounded-2xl border p-2.5 text-left transition-[border-color,background-color] duration-300 lg:w-full ${
                                        current.id === v.id
                                            ? "border-[#E30613]/50 bg-[#E30613]/10"
                                            : dark
                                              ? "border-white/10 bg-white/5 hover:border-white/30"
                                              : "border-slate-200 bg-white hover:border-[#0B1F3A]/25"
                                    }`}
                                >
                                    {consent.media ? (
                                        <img
                                            src={`https://i.ytimg.com/vi/${v.id}/mqdefault.jpg`}
                                            alt=""
                                            loading="lazy"
                                            className="h-14 w-24 shrink-0 rounded-lg object-cover"
                                        />
                                    ) : (
                                        <span className="flex h-14 w-24 shrink-0 items-center justify-center rounded-lg bg-[#0B1F3A]/80">
                                            <Play className="h-5 w-5 text-white/70" />
                                        </span>
                                    )}
                                    <span className="min-w-0">
                                        <span
                                            className={`block truncate text-sm font-bold ${
                                                dark ? "text-white" : "text-[#0B1F3A]"
                                            }`}
                                        >
                                            {v.model}
                                        </span>
                                        <span
                                            className={`block truncate text-xs ${
                                                dark ? "text-white/60" : "text-[#4F5D70]"
                                            }`}
                                        >
                                            {v.title}
                                        </span>
                                    </span>
                                </button>
                            </li>
                        ))}
                    </ul>
                    <div
                        aria-hidden="true"
                        className={`pointer-events-none absolute inset-x-0 bottom-0 hidden h-14 lg:block ${
                            dark
                                ? "bg-gradient-to-t from-[#0B1F3A] to-transparent"
                                : "bg-gradient-to-t from-white to-transparent"
                        }`}
                    />
                    <p className={`mt-2 hidden items-center gap-1.5 text-[11px] lg:flex ${dark ? "text-white/50" : "text-[#94A3B8]"}`}>
                        <ChevronsUpDown className="h-3.5 w-3.5" /> Scorri l'elenco per vedere tutti i video
                    </p>
                    </div>
                </Reveal>
            </div>
        </div>
    );
};
